import CryptoJS from "crypto-js";

/**
 * Encrypts a string using AES-128-CBC algorithm
 * Matches the backend encryption format: IV:encrypted
 *
 * @param {string} text - The text to encrypt
 * @returns {string} - Encrypted string in format "iv:encrypted"
 */
export function encryptString(text) {
  if (!text || typeof text !== "string") {
    return text;
  }

  const algorithm =
    process.env.REACT_APP_CRYPTO_ALGORITHM_TENANT || "aes-128-cbc";
  const encryptionKey = process.env.REACT_APP_CRYPTO_ENCRYPTION_KEY_TENANT;
  // console.log('encryptString: encryptionKey:', encryptionKey)
  // console.log('encryptString: algorithm:', algorithm)

  // For AES-128, key must be 16 bytes (128 bits)
  // Pad or truncate the key to 16 bytes
  const key = CryptoJS.enc.Utf8.parse(
    encryptionKey.padEnd(16, "\0").substring(0, 16),
  );

  // Generate random IV (16 bytes for AES)
  const iv = CryptoJS.lib.WordArray.random(16);

  // Encrypt using AES with CBC mode
  const encrypted = CryptoJS.AES.encrypt(text, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  // Return format: IV:encrypted (both in hex)
  const ivHex = iv.toString(CryptoJS.enc.Hex);
  const encryptedHex = encrypted.ciphertext.toString(CryptoJS.enc.Hex);

  return `${ivHex}:${encryptedHex}`;
}

/**
 * Decrypts a string using AES-128-CBC algorithm
 * Matches the backend decryption format
 *
 * @param {string} text - The encrypted text in format "iv:encrypted"
 * @returns {string} - Decrypted string
 */
export function decryptString(text) {
  if (!text || typeof text !== "string") {
    return text;
  }

  const algorithm =
    process.env.REACT_APP_CRYPTO_ALGORITHM_TENANT || "aes-128-cbc";
  const encryptionKey = process.env.REACT_APP_CRYPTO_ENCRYPTION_KEY_TENANT;
  // console.log('decryptString: encryptionKey:', encryptionKey)
  // console.log('decryptString: algorithm:', algorithm)

  // Split IV and encrypted text
  const [ivString, encryptedText] = text.split(":");

  if (!ivString || !encryptedText) {
    return text; // Return original if format is invalid
  }

  // For AES-128, key must be 16 bytes (128 bits)
  const key = CryptoJS.enc.Utf8.parse(
    encryptionKey.padEnd(16, "\0").substring(0, 16),
  );

  // Convert IV from hex
  const iv = CryptoJS.enc.Hex.parse(ivString);

  // Convert encrypted text from hex
  const encrypted = CryptoJS.enc.Hex.parse(encryptedText);

  // Create cipher params
  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: encrypted,
  });

  // Decrypt
  const decrypted = CryptoJS.AES.decrypt(cipherParams, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return decrypted.toString(CryptoJS.enc.Utf8);
}

/**
 * Encrypts password fields in a request body
 * Supports both plain objects and FormData
 *
 * @param {Object|FormData} body - The request body
 * @param {string[]} passwordFields - Array of field names to encrypt (default: password, oldPassword, newPassword, confirmPassword, password_confirmation)
 * @returns {Object|FormData} - Body with encrypted password fields
 */
export function encryptPasswordFields(
  body,
  passwordFields = [
    "password",
    "oldPassword",
    "newPassword",
    "confirmPassword",
    "password_confirmation",
  ],
) {
  if (!body) {
    return body;
  }

  // Handle FormData
  if (body instanceof FormData) {
    const newFormData = new FormData();

    // Copy all entries
    for (const [key, value] of body.entries()) {
      if (passwordFields.includes(key) && typeof value === "string" && value) {
        // Encrypt password field
        newFormData.append(key, encryptString(value));
      } else {
        // Keep other fields as is
        newFormData.append(key, value);
      }
    }

    return newFormData;
  }

  // Handle plain objects
  if (typeof body === "object" && body !== null) {
    const encryptedBody = { ...body };

    passwordFields.forEach((field) => {
      if (encryptedBody[field] && typeof encryptedBody[field] === "string") {
        encryptedBody[field] = encryptString(encryptedBody[field]);
      }
    });

    return encryptedBody;
  }

  return body;
}
